import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import ClinicalFormShell from '../components/clinical/ClinicalFormShell';
import {
  getClinicalEncounter,
  patchClinicalEncounter,
  deleteClinicalEncounter,
  uploadEncounterAttachment,
  getPublicApiOrigin,
  resolveMediaUrl,
  getAdminSettings,
  getPatientRecord,
} from '../lib/api';
import {
  formatImageUploadError,
  IMAGE_UPLOAD_ACCEPT,
  IMAGE_UPLOAD_FORMATS_LABEL,
  IMAGE_UPLOAD_MAX_MB,
  CLINICAL_EVIDENCE_ACCEPT,
  CLINICAL_EVIDENCE_FORMATS_LABEL,
  validateImageFile,
  validateClinicalEvidenceFile,
} from '../lib/uploadImage';
import {
  resolveClinicalFormTemplate,
  clinicalFormAllowsEncounterFiles,
  clinicalFormVisitsOnly,
} from '../lib/clinicalFormTemplates';
import { toast } from 'sonner';
import { ArrowLeft, FileText, Trash2, Upload } from 'lucide-react';
import { useClinicBranding } from '../context/ClinicBrandingContext';
import { canPrintEvolutionNoteFromForm } from '../lib/clinicalEncounterNav';
import { printEvolutionNote } from '../lib/printEvolutionNote';

function isPdfAttachment(att) {
  if (!att) return false;
  if (att.kind === 'file') return true;
  const ct = String(att.content_type || '').toLowerCase();
  const url = String(att.url || '').toLowerCase();
  const name = String(att.original_name || '').toLowerCase();
  return ct.includes('pdf') || url.endsWith('.pdf') || name.endsWith('.pdf');
}

function captionsFromAttachments(attachments) {
  const map = {};
  (attachments || []).forEach((att) => {
    if (att?.id) map[att.id] = att.caption || '';
  });
  return map;
}

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

function formFieldsFromState({
  encounterDate,
  chiefComplaint,
  notes,
  diagnosis,
  treatment,
}) {
  return {
    encounter_date: encounterDate,
    chief_complaint: chiefComplaint,
    clinical_notes: notes,
    diagnosis,
    treatment_plan: treatment,
  };
}

export default function AdminClinicalEncounter() {
  const { recordId, encounterId } = useParams();
  const navigate = useNavigate();
  const { info: clinicInfo } = useClinicBranding();
  const [enc, setEnc] = useState(null);
  const [encounterDate, setEncounterDate] = useState(todayYmd);
  const [chiefComplaint, setChiefComplaint] = useState('');
  const [notes, setNotes] = useState('');
  const [diagnosis, setDiagnosis] = useState('');
  const [treatment, setTreatment] = useState('');
  const [printing, setPrinting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [captionEdits, setCaptionEdits] = useState({});
  const [allowsFiles, setAllowsFiles] = useState(false);
  const [visitsOnly, setVisitsOnly] = useState(false);

  const applyEncounterToForm = useCallback((data) => {
    setEnc(data);
    setEncounterDate(data.encounter_date || todayYmd());
    setChiefComplaint(data.chief_complaint || '');
    setNotes(data.clinical_notes || '');
    setDiagnosis(data.diagnosis || '');
    setTreatment(data.treatment_plan || '');
    setCaptionEdits(captionsFromAttachments(data.attachments));
  }, []);

  useEffect(() => {
    let alive = true;
    setEnc(null);
    (async () => {
      try {
        const [data, settings, record] = await Promise.all([
          getClinicalEncounter(encounterId),
          getAdminSettings().catch(() => ({})),
          getPatientRecord(recordId).catch(() => null),
        ]);
        if (!alive) return;
        applyEncounterToForm(data);
        const template = resolveClinicalFormTemplate(
          record?.intake_adult,
          settings?.clinical_form_template,
          record?.status || 'draft',
        );
        setAllowsFiles(clinicalFormAllowsEncounterFiles(template));
        setVisitsOnly(clinicalFormVisitsOnly(template));
      } catch {
        if (alive) toast.error('No se pudo cargar la nota de evolución');
      }
    })();
    return () => {
      alive = false;
    };
  }, [encounterId, recordId, applyEncounterToForm]);

  const buildAttachmentUpdates = () => {
    const attachments = enc?.attachments || [];
    if (attachments.length === 0) return undefined;
    return attachments.map((att) => ({
      id: att.id,
      caption: String(captionEdits[att.id] ?? att.caption ?? '').trim().slice(0, 240),
    }));
  };

  const buildSavePayload = () => ({
    ...formFieldsFromState({
      encounterDate,
      chiefComplaint,
      notes,
      diagnosis,
      treatment,
    }),
    status: 'complete',
  });

  const currentFormFields = () =>
    formFieldsFromState({
      encounterDate,
      chiefComplaint,
      notes,
      diagnosis,
      treatment,
    });

  const isDirty = () => {
    if (!enc) return false;
    const cur = currentFormFields();
    const saved = {
      encounter_date: enc.encounter_date || '',
      chief_complaint: enc.chief_complaint || '',
      clinical_notes: enc.clinical_notes || '',
      diagnosis: enc.diagnosis || '',
      treatment_plan: enc.treatment_plan || '',
    };
    return ['encounter_date', 'chief_complaint', 'clinical_notes', 'diagnosis', 'treatment_plan'].some(
      (k) => String(cur[k] ?? '').trim() !== String(saved[k] ?? '').trim(),
    );
  };

  const handleSaveAndPrint = async () => {
    const formFields = currentFormFields();
    if (!enc || !canPrintEvolutionNoteFromForm(enc, formFields)) {
      toast.error(
        visitsOnly
          ? 'Escriba al menos el servicio/motivo o las notas de la cita'
          : 'Escriba al menos motivo, tratamiento, diagnóstico o indicaciones',
      );
      return;
    }
    setPrinting(true);
    try {
      const payload = buildSavePayload();
      const attachmentUpdates = buildAttachmentUpdates();
      if (attachmentUpdates?.length) {
        payload.attachment_updates = attachmentUpdates;
      }
      const updated = await patchClinicalEncounter(encounterId, payload);
      applyEncounterToForm(updated);
      toast.success('Nota guardada');

      if (!visitsOnly) {
        const result = await printEvolutionNote(encounterId, { clinicInfo });
        if (!result.ok) {
          if (result.reason === 'empty_content') {
            toast.error(result.message || 'No hay datos para generar el PDF');
          } else {
            toast.error('Permita ventanas emergentes para abrir el PDF.');
          }
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo guardar la nota');
    } finally {
      setPrinting(false);
    }
  };

  const handleClose = () => {
    if (isDirty()) {
      const leave = window.confirm(
        'Hay cambios sin guardar en la nota. ¿Desea salir sin guardar?',
      );
      if (!leave) return;
    }
    navigate(`/admin/expedientes/${recordId}?tab=encuentros`);
  };

  const handleDelete = async () => {
    if (!enc?.id) return;
    const ok = window.confirm(
      visitsOnly
        ? '¿Eliminar esta cita / nota y sus fotos? Esta acción no se puede deshacer.'
        : '¿Eliminar esta nota de evolución y sus evidencias? Esta acción no se puede deshacer.',
    );
    if (!ok) return;
    setDeleting(true);
    try {
      await deleteClinicalEncounter(enc.id);
      toast.success(visitsOnly ? 'Cita eliminada' : 'Nota eliminada');
      navigate(`/admin/expedientes/${recordId}?tab=encuentros`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar la nota');
    } finally {
      setDeleting(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const check =
      allowsFiles && !visitsOnly ? validateClinicalEvidenceFile(file) : validateImageFile(file);
    if (!check.ok) {
      toast.error(check.message);
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const att = await uploadEncounterAttachment(encounterId, file);
      setEnc((prev) => {
        if (!prev) return prev;
        return { ...prev, attachments: [...(prev.attachments || []), att] };
      });
      setCaptionEdits((prev) => ({ ...prev, [att.id]: att.caption || '' }));
      toast.success('Evidencia subida');
    } catch (err) {
      toast.error(formatImageUploadError(err), { duration: 8000 });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (!enc) {
    return <p className="text-muted-foreground py-12 text-center">Cargando nota de evolución…</p>;
  }

  const attachments = enc.attachments || [];
  const formForPrint = {
    chief_complaint: chiefComplaint,
    clinical_notes: notes,
    diagnosis,
    treatment_plan: treatment,
  };
  const canPrint = canPrintEvolutionNoteFromForm(enc, formForPrint);

  return (
    <div className="space-y-4">
      <Button asChild variant="ghost" size="sm">
        <Link to={`/admin/expedientes/${recordId}`}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          Volver al expediente
        </Link>
      </Button>

      <ClinicalFormShell
        title={visitsOnly ? 'Nota de la cita' : 'Nota de evolución'}
        subtitle={
          visitsOnly
            ? 'Servicio realizado, fecha y fotos de la cita'
            : enc.appointment_id
              ? 'Seguimiento vinculado a una cita agendada'
              : 'Registro de la visita y evidencias'
        }
        stepIndex={0}
        stepCount={1}
        stepTitle={visitsOnly ? 'Nota de la cita' : 'Nota de evolución'}
        footer={
          <>
            {enc.folio ? (
              <p className="text-sm text-muted-foreground mr-auto self-center">
                Folio: <strong>{enc.folio}</strong>
              </p>
            ) : null}
            <Button
              type="button"
              variant="secondary"
              disabled={printing || deleting || !canPrint}
              onClick={handleSaveAndPrint}
            >
              <FileText className="w-4 h-4 mr-1" />
              {printing ? 'Guardando…' : visitsOnly ? 'Guardar' : 'Imprimir / Guardar PDF'}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={printing || deleting}
              onClick={handleDelete}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {deleting ? 'Eliminando…' : 'Eliminar'}
            </Button>
            <Button type="button" variant="outline" disabled={deleting} onClick={handleClose}>
              Cerrar
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="encounter-date">Fecha</Label>
              <Input
                id="encounter-date"
                type="date"
                className="mt-1 h-11"
                value={encounterDate}
                onChange={(e) => setEncounterDate(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="chief-complaint">{visitsOnly ? 'Servicio / motivo' : 'Motivo'}</Label>
              <Input
                id="chief-complaint"
                className="mt-1 h-11"
                value={chiefComplaint}
                onChange={(e) => setChiefComplaint(e.target.value)}
                placeholder={visitsOnly ? 'Corte, color, tratamiento…' : 'Describe'}
                maxLength={500}
              />
            </div>
          </div>
          <div>
            <Label>{visitsOnly ? 'Notas de la cita' : 'Tratamiento / Procedimiento realizado'}</Label>
            <Textarea
              className="mt-1 min-h-[120px]"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={
                visitsOnly
                  ? 'Qué se hizo, productos usados, observaciones…'
                  : 'Procedimientos y evolución de la visita…'
              }
            />
          </div>
          {!visitsOnly ? (
            <>
              <div>
                <Label>Diagnóstico</Label>
                <Textarea className="mt-1" rows={3} value={diagnosis} onChange={(e) => setDiagnosis(e.target.value)} />
              </div>
              <div>
                <Label>Indicaciones</Label>
                <Textarea className="mt-1" rows={3} value={treatment} onChange={(e) => setTreatment(e.target.value)} />
              </div>
            </>
          ) : null}
          <div>
            <Label>
              {visitsOnly
                ? 'Fotos de la cita'
                : allowsFiles
                  ? 'Evidencias del seguimiento (fotos y archivos)'
                  : 'Evidencias (fotos)'}
            </Label>
            <p className="text-xs text-muted-foreground mt-1 mb-3">
              {allowsFiles && !visitsOnly ? CLINICAL_EVIDENCE_FORMATS_LABEL : IMAGE_UPLOAD_FORMATS_LABEL}. Máximo{' '}
              {IMAGE_UPLOAD_MAX_MB} MB.
              {visitsOnly ? (
                <>
                  {' '}
                  Los comentarios de cada foto se guardan al pulsar <strong>Guardar</strong>.
                </>
              ) : (
                <>
                  {' '}
                  Los comentarios de cada evidencia se guardan al usar <strong>Imprimir / Guardar PDF</strong>.
                </>
              )}
              {allowsFiles && !visitsOnly
                ? ' Puede adjuntar fotos clínicas, estudios y archivos PDF.'
                : ''}
            </p>
            <div className="rounded-xl border border-dashed p-4 bg-muted/20 max-w-xl">
              <Label htmlFor="evidence-file">Nueva evidencia</Label>
              <Input
                id="evidence-file"
                type="file"
                accept={allowsFiles && !visitsOnly ? CLINICAL_EVIDENCE_ACCEPT : IMAGE_UPLOAD_ACCEPT}
                className="cursor-pointer mt-1"
                disabled={uploading}
                onChange={onUpload}
              />
            </div>

            {attachments.length > 0 ? (
              <ul className="flex flex-col gap-4 mt-5 list-none p-0 m-0">
                {attachments.map((att) => {
                  const pdf = isPdfAttachment(att);
                  return (
                    <li
                      key={att.id}
                      className="flex flex-col gap-3 rounded-xl border border-border p-4 bg-card"
                    >
                      <a
                        href={resolveMediaUrl(att.url)}
                        target="_blank"
                        rel="noreferrer"
                        className="block rounded-lg border overflow-hidden bg-muted/30 self-start max-w-full sm:max-w-xs"
                      >
                        {pdf ? (
                          <div className="flex items-center gap-2 p-4 text-sm font-medium">
                            <FileText className="w-5 h-5 shrink-0" />
                            <span className="truncate">{att.original_name || 'Documento PDF'}</span>
                          </div>
                        ) : (
                          <img
                            src={`${getPublicApiOrigin()}${att.url}`}
                            alt={att.caption || 'Evidencia clínica'}
                            className="w-full max-h-56 object-contain"
                          />
                        )}
                      </a>
                      <div className="w-full space-y-2">
                        <Label htmlFor={`caption-${att.id}`}>Comentario</Label>
                        <Textarea
                          id={`caption-${att.id}`}
                          rows={2}
                          maxLength={240}
                          placeholder="Descripción de esta evidencia…"
                          value={captionEdits[att.id] ?? att.caption ?? ''}
                          onChange={(e) =>
                            setCaptionEdits((prev) => ({ ...prev, [att.id]: e.target.value }))
                          }
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground mt-4 flex items-center gap-1">
                <Upload className="w-4 h-4 shrink-0" />
                Sin evidencias aún
              </p>
            )}
          </div>
        </div>
      </ClinicalFormShell>
    </div>
  );
}
